<template>
  <div class="k-table-container">
    <div class="k-table-panel" :class="{'panel panel-default': panel}">
      <k-table-controls v-if="showControls"
        :headers="fullHeaders"
        :shown-keys="shownKeys"
        :active-col-selectors="activeColSelectors"
        :downloadable="downloadable"
        :col-selector-options="colSelectorOptions"
        :show-legend="showLegend"
        :hideable="hideable"
        :filtersClearable="filtersClearable"
        @update:shown-keys="onShownKeysChange"
        @update:active-col-selectors="onActiveColSelectorsChange"
        @download="downloadCsv"
        @reset-columns="resetColumns"
        @clear-filters="resetTable"
      />
      <div v-show="enableTopScroll && showTopScroll" ref="topScroll" class="k-table-top-scrollbar" aria-hidden="true">
        <div :style="{ width: topScrollWidth + 'px', height: '1px' }"></div>
      </div>
      <div class="k-table-wrapper" :class="{'sticky-header': stickyHeader}" ref="mainScroll">
        <table class="table">
          <k-table-header
            :headers="niceHeaders"
            :super-headers="superHeaders"
            :column-order="columnOrder"
            :unique-dropdown-options="uniqueDropdownOptions"
            :dropdown-filters="dropdownFilters"
            :search-filters="searchFilters"
            :fixed-column-keys="fixedColumnKeys"
            :get-super-header-col-span="getSuperHeaderColSpan"
            :super-header-keys-iterator="superHeaderKeysIterator"
            :on-dropdown-filter-change="onDropdownFilterChange"
            :on-search-filter-change="onSearchFilterChange"
            :is-ascending-sorting="sortAscending"
            :current-sort-key="currentSortKey"
            @sort="sortCol"
          />

          <tbody>
            <tr v-if="paginatedRows.length === 0">
              <td class="empty-table-row" :colspan="columnOrder.length || 1">
                <p :role="loading ? 'status' : undefined" :aria-label="loading ? 'Loading' : undefined">
                  {{ loading ? '' : emptyMessage }}
                  <i v-if="loading" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                </p>
              </td>
            </tr>
            <k-table-row
              v-for="(row, index) in paginatedRows"
              :key="getRowKey(row, index)"
              :row="row"
              :headers="niceHeaders"
              :column-order="columnOrder"
              :selected="row.index === currentRowIndex"
              :fixed-column-keys="fixedColumnKeys"
              :support-row-click="supportRowClick"
              :class-func="classFunc"
              @row-click="(row) => handleRowClick(row, index)"
              @action-click="emitActionClick"
              @view-full-text="showFullTextModal"
            />
          </tbody>
        </table>
      </div>
      <k-modal
        class="full-text-modal"
        :show="fullTextModal"
        @close="fullTextModal = false"
        role="dialog"
      >
        <template #header>
            <h3>{{ modalContent?.name }}</h3>
        </template>
        <template #body>
            <p class="cell-full-text">{{ modalContent?.fullText }}</p>
        </template>
        <template #footer>
            <button
              class="btn btn-primary"
              @click="fullTextModal = false"
            >
              Close
            </button>
        </template>
      </k-modal>
      <pagination v-if="numOfPages" v-model="currentPage" :count="numOfPages" :total-entries="rows.length" :displayed-entries="paginatedRows.length"></pagination>
    </div>
  </div>
</template>

<style scoped>
.k-table-container tbody tr:nth-child(2n) {
  background-color: var(--kate-background-alpha);
}

table.table {
  border: var(--border-tertiary);
}

.empty-table-row {
  text-align: center;
  padding: 15px;
  font-style: italic;
}

.empty-table-row .fa-spinner {
  font-size: 1.2em;
  margin-left: 10px;
}

.k-table-container {
  position: relative;
}

</style>

<script>
import TopScrollbarMixin from '@mixins/top-scrollbar-mixin';
import format from 'number-format.js';
import TimeMixins from '@mixins/time-mixins';
import CsvMixins from '@mixins/csv-mixins';
import stringMatch from '@utils/string-match';
import { sanitisedHtml } from '@utils/sanitise-html';
import KModal from '@base-components/k-modal.vue';
import TableDropdownOverflowMixin from '@mixins/table-dropdown-overflow-mixin';
import Pagination from '../../k-pagination.vue';
import KTableHeader from './k-table-header.vue';
import KTableControls from './k-table-controls.vue';
import KTableRow from './k-table-row.vue';

const HEADER_TEMPLATE = {
  name: '',
  displayName: undefined,
  type: 'string',
  filterable: false,
  sortable: false,
  trim: false,
  hideable: true,
  hasMultiSelectDropdown: false,
  hasDropdown: false,
  options: {},
  sortBy: undefined,
  displayAs: undefined,
  downloadAs: undefined,
  filterBy: undefined,
  dropdownDisplayAs: undefined,
};

export default {
  props: {
    enableTopScroll: {
      type: Boolean,
      default: false,
    },
    panel: {
      type: Boolean,
      default: true,
    },
    // Header
    headers: Object,
    superHeaders: {
      type: Object,
      default: undefined,
    },
    subHeaderSortFunction: {
      type: Function,
    },
    headerOrder: Array,
    defaultHeaders: {
      type: Array,
    },
    // Rows
    rows: Array,
    // Controls
    downloadable: Boolean,
    filename: String,
    hideable: {
      type: Boolean,
      default: true,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    fixedColumn: {
      type: Number,
      default: undefined,
      validator: value => [undefined, 0, 1, 2].includes(value),
    },
    max: Number,
    orderBy: {
      type: String,
      note: 'key from header',
    },
    dateMask: {
      type: String,
      default: undefined,
    },
    dateFormat: {
      type: String,
      default: undefined,
    },
    timestampMask: {
      type: String,
      default: undefined,
    },
    timestampFormat: {
      type: String,
      default: undefined,
    },
    classFunc: {
      type: Function,
      default: () => '',
    },
    colSelectorOptions: {
      type: Array,
      default: () => [],
    },
    defaultColSelectorIndex: {
      type: Number,
      default: undefined,
    },
    supportRowClick: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: 'No data found',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    stickyHeader: {
      type: Boolean,
      default: false,
    },
    filtersClearable: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [TimeMixins, CsvMixins, TopScrollbarMixin, TableDropdownOverflowMixin],
  components: {
    Pagination,
    KTableHeader,
    KTableControls,
    KTableRow,
    KModal,
  },

  data() {
    return {
      currentPage: 0,
      searchFilters: {},
      currentSortKey: null,
      sortAscending: true,
      pageOffset: 0,
      showSidemenu: false,
      currentRowIndex: undefined,
      activeColSelectors: [],
      rawShownKeys: [],
      dropdownFilters: {},
      fullTextModal: false,
      modalContent: undefined,
      hasDropdownOpen: false,
    };
  },
  beforeMount() {
    this.resetRawShownKeys();
  },

  watch: {
    numOfPages() {
      this.currentPage = 0;
    },
    headers() {
      this.resetRawShownKeys();
    },
    defaultHeaders() {
      this.rawShownKeys = this.defaultHeaders;
      this.activeColSelectors = this.getFullyCoveredGroups(this.rawShownKeys);
    },
    rows: {
      handler() {
        if (this.fixedColumn) {
          this.$nextTick(() => this.getFixedColumnWidths());
        }
      },
      immediate: true,
    },
  },

  computed: {
    rawHeaderKeys() {
      return Object.keys(this.headers);
    },

    shownKeys() {
      return this.rawShownKeys.length > 0 ? this.rawShownKeys : this.rawHeaderKeys;
    },

    headerKeys() {
      return Object.keys(this.niceHeaders);
    },

    superHeaderKeys() {
      if (!this.superHeaders) {
        return undefined;
      }
      return Object.keys(this.superHeaders).sort();
    },

    superHeaderKeysIterator() {
      if (!this.superHeaders) {
        return undefined;
      }
      return this.superHeaderKeys.filter(key => this.getSuperHeaderColSpan(this.superHeaders[key]) > 0);
    },

    niceHeadersKeys() {
      if (this.hasSuperHeaders) {
        let output = [];
        for (let j = 0; j < this.superHeaderKeys.length; j++) {
          const k = this.superHeaderKeys[j];
          const subHeaderKeys = this.rawHeaderKeys.filter(x => this.superHeaders[k].subHeaderKeys.includes(x));
          const subHeaders = [];
          for (let i = 0; i < subHeaderKeys.length; i++) {
            const newSubHeader = this.headers[subHeaderKeys[i]];
            newSubHeader.headerKey = subHeaderKeys[i];
            subHeaders.push(newSubHeader);
          }
          output = output.concat(subHeaders.sort(this.subHeaderSortFunction).map(x => x.headerKey));
        }
        return output;
      }
      return this.headerOrder || this.rawHeaderKeys;
    },

    uniqueDropdownOptions() {
      const options = {};
      this.headerKeys.forEach(key => {
        const header = this.niceHeaders[key];
        if (header.hasMultiSelectDropdown || header.hasDropdown) {
          const values = this.rows.map(row => header.dropdownDisplayAs(row[key])).filter(value => value !== null && value !== undefined);
          options[key] = [...new Set(values)];
        }
      });
      return options;
    },

    indexedRows() {
      return this.rows.map((val, index) => Object.assign(val, {
        index,
      }));
    },

    numOfPages() {
      if (!this.max || this.niceRows.length <= this.max) {
        return undefined;
      }
      return Math.ceil(this.niceRows.length / this.max);
    },

    paginatedRows() {
      if (!this.numOfPages) {
        return this.niceRows;
      }
      const output = [];
      const startingIndex = this.max * this.currentPage;
      const endingIndex = Math.min(this.max + startingIndex, this.niceRows.length);
      for (let i = startingIndex; i < endingIndex; i++) {
        output.push(this.niceRows[i]);
      }
      return output;
    },

    niceRows() {
      return this.indexedRows
        .filter(row => this.isRowVisible(row)) // Check if row is visible from filters
        .sort((a, b) => (this.currentSortKey ? this.sortingFunction(a, b) : 0)); // Apply sorting if sort button pressed
    },

    fullHeaders() {
      const output = {};
      const keys = this.niceHeadersKeys;
      for (let i = 0; i < keys.length; i++) {
        output[keys[i]] = this.getHeader(this.headers[keys[i]]);
      }
      return output;
    },

    hideableHeaders() {
      const output = {};
      const keys = Object.keys(this.fullHeaders);
      for (let i = 0; i < keys.length; i++) {
        if (this.fullHeaders[keys[i]].hideable) {
          output[keys[i]] = this.fullHeaders[keys[i]];
        }
      }
      return output;
    },

    columnOrder() {
      return this.niceHeadersKeys.filter(x => this.shownKeys.indexOf(x) !== -1);
    },

    niceHeaders() {
      const output = {};
      const keys = this.columnOrder;
      for (let i = 0; i < keys.length; i++) {
        if (this.shownKeys.indexOf(keys[i]) !== -1) {
          output[keys[i]] = this.fullHeaders[keys[i]];
        }
      }
      return output;
    },

    sortMultiplier() {
      return this.sortAscending ? 1 : -1;
    },

    hasSuperHeaders() {
      return this.superHeaders !== undefined;
    },

    fixedColumnKeys() {
      if (!this.fixedColumn) return [];
      return this.columnOrder.slice(0, this.fixedColumn);
    },
  },

  methods: {
    isRowVisible(row) {
      let header;
      let key;
      let valToAnalyse;
      for (let i = 0; i < this.headerKeys.length; i++) {
        key = this.headerKeys[i];
        header = this.niceHeaders[key];

        // check if dropdown is selected
        if (this.dropdownFilters[key] && this.dropdownFilters[key].length > 0) {
          valToAnalyse = header.dropdownDisplayAs(row[key]);
          if (!this.dropdownFilters[key].includes(valToAnalyse)) {
            return false;
          }
        }
        // check if there is a search filter
        if (this.searchFilters[key]) {
          valToAnalyse = header.filterBy(row[key]);
          if (!stringMatch(valToAnalyse, this.searchFilters[key])) {
            return false;
          }
        }
      }
      return true; // Visible if not filtered out
    },

    cleanSortString(text) {
      if (!text || typeof text !== 'string') {
        return text;
      }
      return sanitisedHtml(text).trim().toLowerCase();
    },

    cleanDownloadString(text) {
      if (!text || typeof text !== 'string') {
        return text;
      }
      return sanitisedHtml(text).trim();
    },

    onDropdownFilterChange(key, value) {
      this.dropdownFilters[key] = value;
    },

    onSearchFilterChange(key, value) {
      this.searchFilters[key] = value;
    },

    onShownKeysChange(newKeys) {
      this.rawShownKeys = newKeys;
    },

    onActiveColSelectorsChange(newFilters) {
      const currentFilters = this.activeColSelectors;
      this.activeColSelectors = newFilters;

      const keys = new Set(this.rawShownKeys);
      const inGroup = (key, idx) => this.colSelectorOptions[idx].func(this.getHeader(this.headers[key]), key);

      // Add columns from newly checked groups
      newFilters.filter(f => !currentFilters.includes(f)).forEach(idx => {
        this.rawHeaderKeys.filter(key => inGroup(key, idx)).forEach(key => keys.add(key));
      });

      // Remove columns from unchecked groups (unless in another active group)
      currentFilters.filter(f => !newFilters.includes(f)).forEach(idx => {
        this.rawHeaderKeys
          .filter(key => inGroup(key, idx) && !newFilters.some(f => inGroup(key, f)))
          .forEach(key => keys.delete(key));
      });

      this.rawShownKeys = [...keys];
    },

    resetColumns() {
      this.resetRawShownKeys();
    },

    resetFilters() {
      this.searchFilters = {};
      this.dropdownFilters = {};
    },

    resetSorting() {
      this.sortAscending = true;
      this.currentSortKey = this.rawHeaderKeys.includes(this.orderBy) ? this.orderBy : null;
    },

    resetTable() {
      this.resetFilters();
      this.resetSorting();
    },

    getRowKey(row, index) {
      return `${row.index}-${index}`;
    },

    getFullyCoveredGroups(shownKeys) {
      return this.colSelectorOptions
        .map((opt, idx) => {
          const groupKeys = this.rawHeaderKeys.filter(key => opt.func(this.getHeader(this.headers[key]), key));
          return groupKeys.length > 0 && groupKeys.every(key => shownKeys.includes(key)) ? idx : null;
        })
        .filter(idx => idx !== null);
    },

    resetRawShownKeys() {
      // Priority: defaultColSelectorIndex > defaultHeaders > all headers
      const selector = this.colSelectorOptions[this.defaultColSelectorIndex];
      if (selector) {
        this.activeColSelectors = [this.defaultColSelectorIndex];
        this.rawShownKeys = this.rawHeaderKeys.filter(key => selector.func(this.getHeader(this.headers[key]), key));
      } else {
        this.rawShownKeys = this.defaultHeaders || Object.keys(this.headers);
        this.activeColSelectors = this.getFullyCoveredGroups(this.rawShownKeys);
      }
      if (this.rawHeaderKeys.indexOf(this.orderBy) !== -1) {
        this.currentSortKey = this.orderBy;
      }
    },

    toggleSidemenu() {
      this.showSidemenu = !this.showSidemenu;
    },

    emitActionClick(key, index, row, header) {
      this.$emit('clicked', key, index, row, header);
    },

    handleRowClick(row, index) {
      this.currentRowIndex = index;
      this.$emit('row-clicked', row, index);
    },

    nextPage() {
      this.pageOffset += 1;
    },

    prevPage() {
      this.pageOffset -= 1;
    },

    downloadCsv() {
      if (!this.downloadable) {
        return;
      }
      this.downloadCsvFromTable(this.niceHeaders, this.niceRows, this.filename);
    },

    sortingFunction(a, b) {
      let A;
      let B;

      const currentSortHeader = this.niceHeaders[this.currentSortKey];
      // If the current sort key is no longer valid for the current headers,
      // skip sorting to avoid empty/no-data glitches when columns change.
      if (!currentSortHeader) {
        return 0;
      }

      if (currentSortHeader.type === 'function') {
        A = this.niceHeaders[this.currentSortKey].sortBy(a, a.index);
        B = this.niceHeaders[this.currentSortKey].sortBy(b, b.index);
      } else if (currentSortHeader.type === 'rag-dot') {
        const aObj = a[this.currentSortKey];
        const bObj = b[this.currentSortKey];
        A = currentSortHeader.sortBy(aObj, a);
        B = currentSortHeader.sortBy(bObj, b);
      } else {
        const aObj = a[this.currentSortKey];
        const bObj = b[this.currentSortKey];
        A = currentSortHeader.sortBy(aObj);
        B = currentSortHeader.sortBy(bObj);
      }

      // Put entries will nulls at the bottom regardless of sort order
      if (A == null) {
        return 1;
      }
      if (B == null) {
        return -1;
      }
      if (A < B) {
        return -1 * this.sortMultiplier;
      }
      if (A > B) {
        return 1 * this.sortMultiplier;
      }
      return 0;
    },

    sortCol(key) {
      if (this.currentSortKey === key) {
        this.sortAscending = !this.sortAscending;
      } else {
        this.sortAscending = true;
        this.currentSortKey = key;
      }
    },

    getSortBy(header) {
      // if a custom sortBy is defined, use it
      if (header.sortBy) {
        return header.sortBy;
      }
      const defaultSortBy = x => {
        if (x && x.text) {
          return this.cleanSortString(x.text);
        }
        return typeof x === 'string' ? this.cleanSortString(x) : x;
      };

      switch (header.type) {
        case 'date':
          return x => {
            if (!x) return null;
            let dt;
            if (x instanceof Date) {
              dt = x;
            } else if (this.dateMask) {
              dt = this.getDate(x, this.dateMask);
            } else {
              dt = this.getDate(x);
            }
            const unixTimestampMs = dt instanceof Date ? dt.getTime() : NaN;
            return Number.isNaN(unixTimestampMs) ? null : unixTimestampMs; // Push nulls to the bottom
          };
        case 'datetime':
          return x => {
            if (!x || !this.isValidDatetime(x)) return null;
            let dt;
            if (x instanceof Date) {
              dt = x;
            } else {
              dt = this.getDate(x);
            }
            const unixTimestampMs = dt instanceof Date ? dt.getTime() : NaN;
            return Number.isNaN(unixTimestampMs) ? null : unixTimestampMs; // Push nulls to the bottom
          };
        case 'timestamp':
          return x => {
            if (!x) return null;
            let dt;
            if (x instanceof Date) {
              dt = x;
            } else if (this.timestampMask) {
              dt = this.getDate(x, this.timestampMask);
            } else {
              dt = this.getDate(x);
            }
            const unixTimestampMs = dt instanceof Date ? dt.getTime() : NaN;
            return Number.isNaN(unixTimestampMs) ? null : unixTimestampMs; // Push nulls to the bottom
          };
        case 'tags':
          return x => (x && x.tags ? [...x.tags].sort().join(' ') : '');
        case 'function':
          return (row, index) => {
            const display = this.getDisplayAs(header)(row, index);
            return this.cleanSortString(display);
          };
        case 'pill':
          return x => this.cleanSortString(x.text ? x.text : x);
        case 'rag-dot':
          return (x, row) => {
            const metricField = header.options?.metricField || x;
            const metricValue = row?.[metricField] ?? x;
            if (metricValue == null || metricValue === '') return null;
            return metricValue;
          };
        case 'action':
        case 'button':
          return () => header.name.toLowerCase();
        default:
          return defaultSortBy;
      }
    },

    getDisplayAs(header) {
      // if a custom displayAs is defined, use it
      if (header.displayAs) {
        return header.displayAs;
      }

      switch (header.type) {
        case 'date':
          return x => this.parseTimestamp(x, this.dateMask, this.dateFormat) || '-';
        case 'datetime':
          return x => {
            if (!x || !this.isValidDatetime(x)) return '-';
            if (header.options?.datetimeFormat) {
              return this.parseTimestamp(x, undefined, header.options.datetimeFormat);
            }
            return this.parseTimestamp(x);
          };
        case 'timestamp':
          return x => this.parseTimestamp(x, this.timestampMask, this.timestampFormat) || '-';
        case 'percentage':
          return x => this.formatNumber(x, header.options.format || '0.00%') || '-';
        case 'number':
          return x => this.formatNumber(x, header.options.format) || '-';
        case 'tags':
          return x => (x && x.tags ? x.tags.map(tag => `<span class="badge">${tag}</span>`).join('') : '-');
        case 'boolean':
          return x => (x ? '<i class="fas fa-check" aria-label="True"></i>' : '<i class="fas fa-times" aria-label="False"></i>');
        case 'function':
          return (row, index) => (header.options.callback ? header.options.callback(row, index, this.niceHeaders) : '-');
        case 'rag-dot':
          return x => (x == null || x === '' ? '-' : x);
        default:
          return x => (x && x.text ? x.text : x || '-');
      }
    },

    getDownloadAs(header) {
      // if a custom downloadAs is defined, use it
      if (header.downloadAs) {
        return header.downloadAs;
      }
      switch (header.type) {
        case 'boolean':
          return x => (x ? 'True' : 'False');
        case 'string':
        case 'html':
          return x => this.cleanDownloadString(x);
        case 'tags':
          return x => (x && x.tags ? x.tags.join(' ') : '');
        case 'function':
          return (row, index) => {
            const display = this.getDisplayAs(header)(row, index);
            return this.cleanDownloadString(display);
          };
        case 'pill':
          return x => {
            if (x == null) return '-';
            return this.cleanDownloadString(x.text ? x.text : x);
          };
        case 'rag-dot':
          return (x, row) => {
            const { metricField, ragField, metricType } = header.options || {};
            const metricValue = row?.[metricField] ?? x;
            if (metricValue == null || metricValue === '') return '-';
            const metricFormat = { percentage: '0.00%', number: '0.00' }[metricType];
            const display = metricFormat ? this.formatNumber(metricValue, metricFormat) : String(metricValue);
            return row?.[ragField] ? `${row[ragField]} - ${display}` : display;
          };
        case 'action':
        case 'button':
          return () => header.name;
        default:
          return this.getDisplayAs(header);
      }
    },

    getRagDotStatus(header, value) {
      if (header.type !== 'rag-dot' || value == null || value === '') return null;
      const ragStatusFn = header.options?.ragStatus;
      return ragStatusFn && typeof ragStatusFn === 'function' ? ragStatusFn(value) : null;
    },

    getFilterBy(header) {
      // if a custom filterBy is defined, use it
      if (header.filterBy) {
        return header.filterBy;
      }
      if (header.type === 'rag-dot') {
        return x => {
          const ragStatus = this.getRagDotStatus(header, x);
          return ragStatus ? this.cleanSortString(ragStatus) : null;
        };
      }
      // Use sortBy for consistency
      return this.getSortBy(header);
    },

    getDropdownDisplayAs(header) {
      if (header.dropdownDisplayAs) {
        return header.dropdownDisplayAs;
      }
      if (header.type === 'rag-dot') {
        return x => {
          const ragStatus = this.getRagDotStatus(header, x);
          return ragStatus ? ragStatus.charAt(0).toUpperCase() + ragStatus.slice(1).toLowerCase() : null;
        };
      }
      return this.getDisplayAs(header);
    },

    formatNumber(num, fmt) {
      if (num === null || num === undefined) {
        return '-';
      }
      return format(fmt || '0.00', num);
    },

    getHeader(header) {
      const headerOptions = Object.keys(HEADER_TEMPLATE);
      const output = {};
      for (let i = 0; i < headerOptions.length; i++) {
        if (headerOptions[i] in header) {
          output[headerOptions[i]] = header[headerOptions[i]];
        } else {
          output[headerOptions[i]] = HEADER_TEMPLATE[headerOptions[i]];
        }
      }
      output.sortBy = this.getSortBy(output);
      output.displayAs = this.getDisplayAs(output);
      output.downloadAs = this.getDownloadAs(output);
      output.filterBy = this.getFilterBy(output);
      output.dropdownDisplayAs = this.getDropdownDisplayAs(output);
      return output;
    },

    getSuperHeaderColSpan(superHeader) {
      return superHeader.subHeaderKeys.filter(x => this.shownKeys.includes(x)).length;
    },

    showFullTextModal(key, cellData) {
      if (!key || !cellData) return;
      this.fullTextModal = true;
      const fullText = this.getDisplayAs(this.headers[key])(cellData);
      this.modalContent = {
        name: this.headers[key]?.displayName || this.headers[key]?.name || key,
        fullText,
      };
    },

    getFixedColumnWidths() {
      const kTableWrapper = this.$refs.mainScroll;
      const topScrollbar = this.$refs.topScroll;
      const headerRow = kTableWrapper?.querySelector('table thead tr:last-child');
      if (!headerRow) return;

      const fixedHeaders = Array.from(headerRow.children).slice(0, this.fixedColumn);
      let totalWidth = 0;

      fixedHeaders.forEach((th, i) => {
        const width = th.getBoundingClientRect().width;
        kTableWrapper.style.setProperty(`--fixed-col-${i + 1}-width`, `${width}px`);
        if (i > 0) kTableWrapper.style.setProperty(`--fixed-col-${i + 1}-left`, `${totalWidth}px`);
        totalWidth += width;
      });
      kTableWrapper.style.setProperty('--fixed-columns-width', `${totalWidth}px`);
      if (topScrollbar) {
        topScrollbar.style.setProperty('--fixed-columns-width', `${totalWidth}px`);
      }
    },
  },
};
</script>
